import { black } from '@pelotoncycle/design-system';
import React from 'react';
import ReactModal from 'react-modal';
import { createGlobalStyle } from 'styled-components';
import { ieHideScroll, media } from '@peloton/styles';

export const TIMEOUT_DURATION = 400;

export type CloseHandlers = {
  handleRequestClose: () => void;
  handleRequestBack?: () => void;
};

export type Props = CloseHandlers & {
  children: ({ handleRequestClose, handleRequestBack }: CloseHandlers) => JSX.Element;
  isOpen: boolean;
  backgroundColor: string;
  ariaLabel: string;
  handleRequestClose: () => void;
  handleRequestBack?: () => void;
};

// Note: the majority of this code comes from @ecomm/flyout and @peloton/flyout.
//The right-to-left functionality is really baked in to both components, so it seems easier to just create a new version of it for the nav vs editing both packages to accommodate a single top-to-bottom flyout.

// http://reactcommunity.org/react-modal/accessibility/#app-element
const root = document.getElementById('root') as HTMLElement;
ReactModal.setAppElement(root ?? document.body);

const NavFlyout = ({
  children,
  isOpen,
  backgroundColor,
  handleRequestClose,
  ariaLabel,
  handleRequestBack,
}: Props) => {
  const handleCloseAndShiftDriftChat = () => {
    handleRequestClose();
  };

  const handleAnimationsAndShiftDriftChat = () => {
    const panelItems = document.querySelectorAll('.nav-panel-item');
    setTimeout(() => {
      panelItems?.forEach((item, index) =>
        item.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 150,
          fill: 'forwards',
          easing: 'ease-in',
          delay: (index + 1) * 50,
        }),
      ),
        300;
    });
  };

  return (
    <ReactModal
      className="flyout-content"
      overlayClassName="flyout-overlay"
      onRequestClose={handleCloseAndShiftDriftChat}
      onAfterOpen={handleAnimationsAndShiftDriftChat}
      isOpen={isOpen}
      closeTimeoutMS={TIMEOUT_DURATION}
      contentLabel={ariaLabel}
    >
      <GlobalModalStyles
        backgroundColor={backgroundColor}
        isSecondaryPanel={Boolean(handleRequestBack)}
      />
      {children({ handleRequestClose: handleCloseAndShiftDriftChat, handleRequestBack })}
    </ReactModal>
  );
};

const GlobalModalStyles = createGlobalStyle<{
  backgroundColor: string;
  isSecondaryPanel: boolean;
}>`
  .flyout-content {
    position: fixed;
    background: ${props => props.backgroundColor};
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    outline: none;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    border: none;
    transform: ${({ isSecondaryPanel }) =>
      `translate3d(${isSecondaryPanel ? `100%, 0, 0` : `0, -100%, 0`})`};
    transition: 400ms ease;
    z-index: 90005;
    width: 100%;

    ${ieHideScroll}

    ${media.tablet`
      transition: 400ms ease;
    `}
  }

  .ReactModal__Content--after-open {
    transform: translate3d(0, 0, 0);
  }

  .ReactModal__Content--before-close {
    transform: ${({ isSecondaryPanel }) =>
      `translate3d(${isSecondaryPanel ? `100%, 0, 0` : `0, -100%, 0`})`};
  }

  .flyout-overlay {
    &::before {
      content: "";

      /*
        safari specific hack for animating pseudoelements
        see https://stackoverflow.com/questions/16348489/is-there-a-css-hack-for-safari-only-not-chrome
      */
      @media not all and (min-resolution:.001dpcm) {
        @media {
          content: none;
        }
      }

      position: fixed;
      z-index: 90005;
      width: 100%;
      height: 100%;
      top: 0;
      right: 0;
      bottom: 0;
      background-color: ${black};

      transition: opacity 500ms ease;
      opacity: 0;

      ${media.tablet`
        transition: opacity 600ms ease;
      `}
    }
  }

  .ReactModal__Overlay--after-open{
    &::before {
      opacity: 0.3;

      /*
        safari specific hack for animating pseudoelements
        see https://stackoverflow.com/questions/16348489/is-there-a-css-hack-for-safari-only-not-chrome
      */
      @media not all and (min-resolution:.001dpcm) {
        @media {
          content: "";
        }
      }
    }
  }

  .ReactModal__Overlay--before-close{
    &::before {
      opacity: 0;
    }
  }

  .ReactModal__Body--open {
    overflow-y: hidden;
  }
`;

export default NavFlyout;
